// pages/Notifications/ActivityLog.jsx
import React, { useEffect, useState } from "react";
import { apiClient } from "../apiclient/apiclient";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageContainer from "../components/PageContainer";
import Pagination from "../components/Pagination";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Search, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import ThemedTable from "../components/ThemedTable";

const ActivityLog = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filter States
  const [search, setSearch] = useState("");
  const [activityType, setActivityType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    loadLogs(page, search, activityType, startDate, endDate);
  }, [page]);

  const loadLogs = async (p, searchVal = search, typeVal = activityType, startVal = startDate, endVal = endDate) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", p);
      params.append("limit", "50");
      if (searchVal) params.append("search", searchVal);
      if (typeVal && typeVal !== "all") params.append("activityType", typeVal);
      if (startVal) params.append("startDate", startVal);
      if (endVal) params.append("endDate", endVal);

      const res = await apiClient.get(
        `/notifications/activity?${params.toString()}`,
      );
      setLogs(res.data.data);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error("Failed to load activity logs", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = () => {
    setPage(1);
    loadLogs(1, search, activityType, startDate, endDate);
  };

  const handleResetFilters = () => {
    setSearch("");
    setActivityType("all");
    setStartDate("");
    setEndDate("");
    setPage(1);
    loadLogs(1, "", "all", "", "");
  };

  const handleActivityClick = (log) => {
    if (!log.data) return;

    switch (log.activityType) {
      case "order":
        if (log.data.orderId) {
          navigate(`/orders/track-orders?id=${log.data.orderId}`);
        }
        break;
      case "client":
        if (log.data.clientId) {
          navigate(`/manage-client/client?highlight=${log.data.clientId}`);
        }
        break;
      case "product":
        if (log.data.productId) {
          navigate(`/manage-products/products?highlight=${log.data.productId}`);
        } else {
          navigate("/manage-products/products");
        }
        break;
      case "payment":
        if (log.data.clientId) {
          navigate(`/manage-client/client-ledger/${log.data.clientId}`);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="w-full min-h-screen ">
      <div className="flex">
        <Sidebar />
        <div className="flex-1">
          <Header />
          <div className="flex-1 w-full">
            <PageContainer>
              <div className="min-h-full bg-white">
                {/* HEADER */}
                <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50">
                  <h1 className="text-xl font-bold text-slate-800">
                    Activity Log
                  </h1>
                  <p className="text-sm text-slate-500 mt-1">
                    Track all administrative and system activities in real-time
                  </p>
                </div>

                {/* CONTROL BAR (LIGHT BLUE SECTION CLONED FROM SALES REPORT) */}
                <div className="border-b bg-[#EFF6FF] p-3">
                  <div className="flex flex-col lg:flex-row items-center gap-3">
                    {/* FROM DATE */}
                    <div className="flex items-center gap-2 w-full lg:w-auto">
                      <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
                        From
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-1.5 text-sm bg-white outline-none transition w-full lg:w-40 shadow-sm"
                      />
                    </div>

                    {/* TO DATE */}
                    <div className="flex items-center gap-2 w-full lg:w-auto">
                      <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
                        To
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-1.5 text-sm bg-white outline-none transition w-full lg:w-40 shadow-sm"
                      />
                    </div>

                    {/* TYPE DROPDOWN */}
                    <div className="flex items-center gap-2 w-full lg:w-auto">
                      <label className="text-sm font-semibold text-[#172554] whitespace-nowrap">
                        Type
                      </label>
                      <select
                        value={activityType}
                        onChange={(e) => setActivityType(e.target.value)}
                        className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full lg:w-36 shadow-sm cursor-pointer font-medium text-slate-700"
                      >
                        <option value="all">All Types</option>
                        <option value="order">Order</option>
                        <option value="product">Product</option>
                        <option value="client">Client</option>
                        <option value="payment">Payment</option>
                        <option value="user">User</option>
                        <option value="system">System</option>
                      </select>
                    </div>

                    {/* SEARCH INPUT */}
                    <div className="relative w-full lg:flex-1">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Search className="w-4 h-4 text-slate-400" />
                      </span>
                      <input
                        type="text"
                        placeholder="Search by keyword, ID, or name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleFilterSubmit()}
                        className="pl-9 pr-4 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition shadow-sm"
                      />
                    </div>

                    {/* ACTIONS */}
                    <div className="flex gap-2 w-full lg:w-auto shrink-0 justify-end">
                      {/* SUBMIT BUTTON */}
                      <button
                        type="button"
                        onClick={handleFilterSubmit}
                        disabled={loading}
                        className="w-9 h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
                        title="Search Activity Logs"
                      >
                        {loading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Search size={18} />
                        )}
                      </button>

                      {/* RESET BUTTON */}
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        disabled={loading}
                        className="w-9 h-9 bg-[#172554] text-white rounded-lg hover:bg-[#1e2f6a] transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
                        title="Reset Filters"
                      >
                        <RotateCcw size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* CONTENT */}
                <div className="bg-white">
                  {logs.length === 0 ? (
                    <p className="p-4 text-gray-500 text-sm">No activity yet</p>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <ThemedTable className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase text-xs tracking-wide">
                          <tr>
                            <th className="px-4 py-3 text-left">Date & Time</th>
                            <th className="px-4 py-3 text-left">Type</th>
                            <th className="px-4 py-3 text-left min-w-[150px]">Title</th>
                            <th className="px-4 py-3 text-left min-w-[250px]">Activity Detail</th>
                            <th className="px-4 py-3 text-left">Performed By</th>
                            <th className="px-4 py-3 text-center">Link</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log) => (
                            <tr
                              key={log._id}
                              onClick={() => handleActivityClick(log)}
                              className="hover:bg-blue-50/50 cursor-pointer transition-all border-b border-slate-100 last:border-0"
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-slate-500">
                                {format(new Date(log.createdAt), "dd MMM, hh:mm a")}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${
                                    log.activityType === "order"
                                      ? "bg-blue-50 text-blue-600 border-blue-100"
                                      : log.activityType === "client"
                                      ? "bg-purple-50 text-purple-600 border-purple-100"
                                      : log.activityType === "product"
                                      ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                      : log.activityType === "payment"
                                      ? "bg-amber-50 text-amber-600 border-amber-100"
                                      : "bg-slate-50 text-slate-600 border-slate-100"
                                  }`}
                                >
                                  {log.activityType || "System"}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-semibold text-slate-800">
                                {log.title}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {log.message}
                              </td>
                              <td className="px-4 py-3">
                                {log.performedBy ? (
                                  <div className="flex flex-col">
                                    <span className="font-medium text-slate-700">{log.performedBy.name}</span>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                                      {log.performedBy.role}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {log.data && Object.keys(log.data).length > 0 && (
                                  <ExternalLink size={16} className="text-blue-500 mx-auto" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </ThemedTable>
                    </div>
                  )}
                </div>

                  {/* PAGINATION */}
                  {!loading && logs.length > 0 && (
                    <div className="mt-4 pb-10">
                      <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={(p) => setPage(p)}
                      />
                    </div>
                  )}
                </div>
            </PageContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
